import React from "react";
import classNames from "classnames";
import { ChevronIconWithDirection } from "components/icons/icons";

export interface TextButtonProps {
  /** Button text label */
  label: string;
  /** Optional href - if provided, renders as Link, otherwise as button */
  href?: string;
  /** Click handler */
  onClick?: () => void;
  /** Icon position - 'left', 'right', or 'none' */
  iconPosition?: "left" | "right" | "none";
  /** Color variant */
  variant?:
    | "highlightCerise"
    | "highlightPro"
    | "standard"
    | "sifted"
    | "pro"
    | "neutral"
    | "fintech"
    | "startuplife"
    | "climate"
    | "upround"
    | "deeptech"
    | "summit";
  /** Visual state variant used by the design system */
  state?: "default" | "hover" | "active" | "focused" | "inactive";
  /** Additional CSS classes */
  className?: string;
  /** ARIA label for accessibility */
  ariaLabel?: string;
  /** Disabled state */
  disabled?: boolean;
}

type TextButtonVariant = NonNullable<TextButtonProps["variant"]>;
type TextButtonState = NonNullable<TextButtonProps["state"]>;

type VariantStyle = {
  text: string;
  hoverBg: string;
  activeBg: string;
  interactiveHoverBg: string;
  interactiveActiveBg: string;
  focusedBorder: string;
};

const VARIANT_STYLE_MAP: Record<TextButtonVariant, VariantStyle> = {
  highlightCerise: {
    text: "text-sifted",
    hoverBg: "bg-sifted-opacity-10",
    activeBg: "bg-sifted-20",
    interactiveHoverBg: "hover:bg-sifted-opacity-10",
    interactiveActiveBg: "active:bg-sifted-20",
    focusedBorder: "border-sifted",
  },
  highlightPro: {
    text: "text-pro",
    hoverBg: "bg-pro-opacity-10",
    activeBg: "bg-pro-20",
    interactiveHoverBg: "hover:bg-pro-opacity-10",
    interactiveActiveBg: "active:bg-pro-20",
    focusedBorder: "border-pro",
  },
  standard: {
    text: "text-black-rock",
    hoverBg: "bg-black-rock-opacity-10",
    activeBg: "bg-mono-20",
    interactiveHoverBg: "hover:bg-black-rock-opacity-10",
    interactiveActiveBg: "active:bg-mono-20",
    focusedBorder: "border-black-rock",
  },
  sifted: {
    text: "text-sifted",
    hoverBg: "bg-sifted-opacity-10",
    activeBg: "bg-sifted-20",
    interactiveHoverBg: "hover:bg-sifted-opacity-10",
    interactiveActiveBg: "active:bg-sifted-20",
    focusedBorder: "border-sifted",
  },
  pro: {
    text: "text-pro",
    hoverBg: "bg-pro-opacity-10",
    activeBg: "bg-pro-20",
    interactiveHoverBg: "hover:bg-pro-opacity-10",
    interactiveActiveBg: "active:bg-pro-20",
    focusedBorder: "border-pro",
  },
  fintech: {
    text: "text-fintech",
    hoverBg: "bg-fintech-opacity-10",
    activeBg: "bg-fintech-20",
    interactiveHoverBg: "hover:bg-fintech-opacity-10",
    interactiveActiveBg: "active:bg-fintech-20",
    focusedBorder: "border-fintech",
  },
  startuplife: {
    text: "text-startuplife",
    hoverBg: "bg-startuplife-opacity-10",
    activeBg: "bg-startuplife-20",
    interactiveHoverBg: "hover:bg-startuplife-opacity-10",
    interactiveActiveBg: "active:bg-startuplife-20",
    focusedBorder: "border-startuplife",
  },
  climate: {
    text: "text-climate",
    hoverBg: "bg-climate-opacity-10",
    activeBg: "bg-climate-20",
    interactiveHoverBg: "hover:bg-climate-opacity-10",
    interactiveActiveBg: "active:bg-climate-20",
    focusedBorder: "border-climate",
  },
  upround: {
    text: "text-upround",
    hoverBg: "bg-upround-opacity-10",
    activeBg: "bg-upround-20",
    interactiveHoverBg: "hover:bg-upround-opacity-10",
    interactiveActiveBg: "active:bg-upround-20",
    focusedBorder: "border-upround",
  },
  deeptech: {
    text: "text-deeptech",
    hoverBg: "bg-deeptech-opacity-10",
    activeBg: "bg-deeptech-20",
    interactiveHoverBg: "hover:bg-deeptech-opacity-10",
    interactiveActiveBg: "active:bg-deeptech-20",
    focusedBorder: "border-deeptech",
  },
  neutral: {
    text: "text-black-rock",
    hoverBg: "bg-black-rock-opacity-10",
    activeBg: "bg-mono-20",
    interactiveHoverBg: "hover:bg-black-rock-opacity-10",
    interactiveActiveBg: "active:bg-mono-20",
    focusedBorder: "border-black-rock",
  },
  summit: {
    text: "text-black-rock",
    hoverBg: "bg-black-rock-opacity-10",
    activeBg: "bg-mono-20",
    interactiveHoverBg: "hover:bg-black-rock-opacity-10",
    interactiveActiveBg: "active:bg-mono-20",
    focusedBorder: "border-black-rock",
  },
};

const FOCUS_RING_MAP: Record<TextButtonVariant, string> = {
  highlightCerise: "focus-visible:ring-sifted",
  highlightPro: "focus-visible:ring-pro",
  standard: "focus-visible:ring-black-rock",
  sifted: "focus-visible:ring-sifted",
  pro: "focus-visible:ring-pro",
  fintech: "focus-visible:ring-fintech",
  startuplife: "focus-visible:ring-startuplife",
  climate: "focus-visible:ring-climate",
  upround: "focus-visible:ring-upround",
  deeptech: "focus-visible:ring-deeptech",
  neutral: "focus-visible:ring-black-rock",
  summit: "focus-visible:ring-black-rock",
};

const getEffectiveState = (
  state: TextButtonState,
  disabled: boolean
): TextButtonState => {
  if (disabled) {
    return "inactive";
  }

  return state;
};

const getContainerStateClasses = (
  effectiveState: TextButtonState,
  variant: TextButtonVariant
) => {
  if (effectiveState === "focused") {
    return classNames(
      "border-2 px-1",
      VARIANT_STYLE_MAP[variant].focusedBorder
    );
  }

  return "";
};

const getInnerStateClasses = (
  effectiveState: TextButtonState,
  variant: TextButtonVariant
) => {
  if (effectiveState === "hover") {
    return VARIANT_STYLE_MAP[variant].hoverBg;
  }

  if (effectiveState === "active") {
    return VARIANT_STYLE_MAP[variant].activeBg;
  }

  return "";
};

/**
 * TextButton Component
 *
 * A reusable text button component from the Figma design system.
 * Can render as a button or Next.js Link based on href prop.
 *
 * Features:
 * - Optional chevron icon (left or right)
 * - Multiple color variants matching brand themes
 * - Underlined text with hover opacity transition
 * - Accessible with ARIA labels
 * - Presentational pattern - parent controls logic
 *
 * @component
 * @example
 * // As link with right chevron
 * <TextButton
 *   label="Read more"
 *   href="/article"
 *   variant="sifted"
 *   iconPosition="right"
 * />
 *
 * @example
 * // As button with left chevron
 * <TextButton
 *   label="Go back"
 *   onClick={handleClick}
 *   variant="neutral"
 *   iconPosition="left"
 * />
 *
 * @example
 * // No icon
 * <TextButton
 *   label="Learn more"
 *   href="/learn"
 *   variant="pro"
 *   iconPosition="none"
 * />
 */
export const TextButton: React.FC<TextButtonProps> = ({
  label,
  href,
  onClick,
  iconPosition = "right",
  variant = "sifted",
  state = "default",
  className,
  ariaLabel,
  disabled = false,
}) => {
  const safeVariant = variant in VARIANT_STYLE_MAP ? variant : "sifted";
  const effectiveState = getEffectiveState(state, disabled);
  const variantStyle = VARIANT_STYLE_MAP[safeVariant];
  const isInactive = effectiveState === "inactive";

  const baseClasses = classNames(
    "inline-flex items-center gap-1",
    "font-emphasis-14",
    variantStyle.text,
    "focus-visible:outline-none focus-visible:ring-2",
    FOCUS_RING_MAP[safeVariant],
    getContainerStateClasses(effectiveState, safeVariant),
    "transition-colors",
    {
      "text-mono-30 cursor-not-allowed": isInactive,
    },
    className
  );

  const innerClasses = classNames(
    "inline-flex items-center gap-1 px-1 py-0.5",
    "underline",
    "transition-colors",
    getInnerStateClasses(effectiveState, safeVariant),
    {
      [variantStyle.interactiveHoverBg]:
        !isInactive && effectiveState === "default",
      [variantStyle.interactiveActiveBg]:
        !isInactive && effectiveState === "default",
      "hover:underline": !isInactive,
    }
  );

  const content = (
    <span className={innerClasses}>
      {iconPosition === "left" && (
        <ChevronIconWithDirection direction="left" className="h-5 w-5" />
      )}
      <span>{label}</span>
      {iconPosition === "right" && (
        <ChevronIconWithDirection direction="right" className="h-5 w-5" />
      )}
    </span>
  );

  // Render as Link when href is provided
  if (href && !disabled) {
    return (
      <a
        href={href}
        onClick={onClick}
        className={baseClasses}
        aria-label={ariaLabel || label}
      >
        {content}
      </a>
    );
  }

  // Render as button when href is undefined or disabled
  return (
    <button
      type="button"
      onClick={onClick}
      className={baseClasses}
      aria-label={ariaLabel || label}
      disabled={disabled}
    >
      {content}
    </button>
  );
};

TextButton.displayName = "TextButton";

export default TextButton;
